import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { WMICAddPayoutComponent } from 'wmic-pe-capability-gateway-quoteandbind-pa-react';
import { useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import {
    CONSTANTS,
    JobType,
    PAConstants,
    WMICLogger,
    WMICRPCUtil,
    WMICVariousUtil,
} from 'wmic-pe-portals-utils-js';
import { WMICButton } from 'wmic-pe-components-platform-react';
import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';
import { UISupportingInfoLookupService } from 'wmic-pe-capability-supportinginfo';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { PRIOR_LOSS_SOURCE_CODES } from 'wmic-pe-portals-utils-js/StringConstants';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import messages from './WMICAddLossHistoryComponent.messages';
import metadata from './WMICAddLossHistoryComponent.metadata.json5';

import { IntlContext, TranslatorContext } from '@jutro/legacy/locale';

function WMICAddLossHistoryComponent(props) {
    const {
        id,
        value: selectedPriorLoss,
        onValidate,
        submissionVM: jobVM,
        carriersList,
        vehicleOperators,
        availableVehicles,
        isEditing,
        isAdding,
        showErrors,
    } = props;

    const intlContext = useContext(IntlContext);
    const translator = useContext(TranslatorContext);
    const { setWizardLoading, showConfirm } = useWizardModals();
    const { onValidate: setComponentValidation, isComponentValid, registerComponentValidation } = useValidation(id);
    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const history = useHistory();

    const [manualLossTypeAvailableValues, setManualLossTypeAvailableValues] = useState([]);
    const [claimPaymentTypeAvailableValues, setClaimPaymentTypeAvailableValues] = useState([]);

    const jobNumber =
        jobVM?.baseData?.jobType?.value?.code === JobType.SUBMISSION ? jobVM.quoteID.value : jobVM.jobID.value;
    const vehicleChoiceOther = 'other';

    const isMinimumPayoutValid = useCallback(
        () => selectedPriorLoss?.payments?.value?.length > 0,
        [selectedPriorLoss?.payments?.value?.length]
    );

    const areAllPayoutsValid = useCallback(
        () => selectedPriorLoss?.payments?.aspects?.valid && selectedPriorLoss?.payments?.aspects?.subtreeValid,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedPriorLoss?.value?.lossDate]
    );

    const checkComponentValidation = useCallback(
        () => isMinimumPayoutValid() && areAllPayoutsValid(),
        [isMinimumPayoutValid, areAllPayoutsValid]
    );

    useEffect(() => {
        registerComponentValidation(checkComponentValidation);
    }, [checkComponentValidation, registerComponentValidation]);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
        return () => (onValidate ? onValidate(true, id) : undefined);
    }, [isComponentValid, onValidate, id]);

    const retrieveAvailableLossCauses = useCallback(async () => {
        try {
            setWizardLoading(true, translator(commonMessages.loading));
            const result = await UISupportingInfoLookupService.retrieveAvailableLossCauses(
                jobNumber,
                selectedPriorLoss?.value?.lossDate,
                authHeader
            );

            const retrievedAvailableManualLossTypes = result?.claimLossTypes ?? [];
            setManualLossTypeAvailableValues(
                selectedPriorLoss?.manualLossType?.aspects?.availableValues.filter(manualLossType =>
                    retrievedAvailableManualLossTypes.includes(manualLossType.code)
                )
            );
            if (!retrievedAvailableManualLossTypes.includes(selectedPriorLoss?.manualLossType?.value?.code)) {
                selectedPriorLoss.manualLossType.value = null;
            }

            const retrievedAvailableClaimPaymentTypes = result?.claimPaymentTypes ?? [];
            setClaimPaymentTypeAvailableValues(retrievedAvailableClaimPaymentTypes);
            selectedPriorLoss?.payments?.value?.forEach(payout => {
                if (!retrievedAvailableClaimPaymentTypes.includes(payout.type)) {
                    // eslint-disable-next-line no-param-reassign
                    payout.type = null;
                }
            });
        } catch (err) {
            WMICLogger.error('Error retrieving available loss causes', err);
            WMICErrorHandler.processAsModal(translator(messages.somethingWentWrong)).catch(() =>
                history.push(WMICVariousUtil.getRedirectURLForJob(jobVM))
            );
        } finally {
            setWizardLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobNumber, selectedPriorLoss?.value?.lossDate]);

    useEffect(() => {
        if (isEditing) {
            retrieveAvailableLossCauses(); // do nothing - returned promise ignored intentionally
        } else {
            setManualLossTypeAvailableValues(selectedPriorLoss?.manualLossType?.aspects?.availableValues);
        }
    }, [selectedPriorLoss, isEditing, retrieveAvailableLossCauses]);

    if (selectedPriorLoss?.paPriorLoss?.value && !selectedPriorLoss.paPriorLoss.value.vehiclePublicID && !isAdding) {
        selectedPriorLoss.paPriorLoss.vehiclePublicID.value = vehicleChoiceOther;
    }

    const addNewPayout = () => {
        const newPayout = viewModelService.create(
            {
                claimOpenReserves: { amount: undefined, currency: 'cad' },
                lossTotalPaid: { amount: undefined, currency: 'cad' },
            },
            'pc',
            'wmic.edge.ca.capabilities.policyjob.lob.common.draft.dto.insurancehistory.PolicyClaimPayment_WMICDTO',
            { ...selectedPriorLoss.aspects.context(), IsReadOnly: selectedPriorLoss?.value?.isReadOnly ?? false }
        );

        selectedPriorLoss.payments.value.push(newPayout.value);
        return newPayout;
    };

    const removePayout = async (payout, index) => {
        const response = await showConfirm({
            title: messages.removePayoutTitle,
            message: translator(messages.removePayoutMsg),
        });

        if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
            const payments = selectedPriorLoss?.payments?.value;
            payments.splice(index, 1);
            refreshData();
        }
    };

    const savePayout = payout => {
        if (payout?.value?.claimOpenReserves && payout.value.claimOpenReserves.amount == null) {
            // eslint-disable-next-line no-param-reassign
            payout.value.claimOpenReserves.amount = undefined;
        }

        if (payout?.value?.lossTotalPaid && payout.value.lossTotalPaid.amount == null) {
            // eslint-disable-next-line no-param-reassign
            payout.value.lossTotalPaid.amount = undefined;
        }
        return true;
    };

    const isVehicleValid =
        selectedPriorLoss?.paPriorLoss?.aspects?.valid && selectedPriorLoss?.paPriorLoss?.aspects?.subtreeValid;

    const getTypeDisplayName = data => {
        if (selectedPriorLoss?.value?.sourceType === PRIOR_LOSS_SOURCE_CODES.CLAIM_CENTER) {
            return data.value.typeDescription ?? '';
        }

        if (data?.type?.value?.name) {
            return translator({ id: data.type.value.name });
        }

        return '';
    };

    const getStatusDisplayName = data => {
        if (data?.status?.value?.code) {
            return translator({
                id: `typekey.ClaimStatus_WMIC.${_.get(data, 'status.value.code')}`,
            });
        }

        const priorLossStatus = selectedPriorLoss?.status?.value;
        if (priorLossStatus?.name) {
            return translator({
                id: priorLossStatus.name,
            });
        }
        return '';
    };

    const getMonetaryAmountDisplayName = monetaryValue =>
        intlContext.formatNumber(monetaryValue?.amount?.value ?? 0, {
            style: 'currency',
            currency: monetaryValue?.currency?.value?.code,
            currencyDisplay: 'symbol',
        });

    const getClaimOpenReservesDisplayName = data => getMonetaryAmountDisplayName(data?.claimOpenReserves);

    const getLossTotalPaidDisplayName = data => getMonetaryAmountDisplayName(data?.lossTotalPaid);

    const isExistingVehicleSelected = () =>
        ![vehicleChoiceOther, undefined].includes(selectedPriorLoss?.paPriorLoss?.vehiclePublicID?.value);

    const isNoVehicleSelected = () => selectedPriorLoss?.paPriorLoss?.vehiclePublicID?.value === undefined;

    const updatePriorLossVehicleInfo = () => {
        const vehicleID = selectedPriorLoss?.paPriorLoss?.vehiclePublicID?.value;

        if (isExistingVehicleSelected()) {
            const selectedVehicle = availableVehicles.find(vehicle => vehicle.publicID === vehicleID);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleBusinessSegment.value', selectedVehicle?.businessSegment_WMIC);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleType.value', selectedVehicle?.vehicleType);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleYear.value', selectedVehicle?.year);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleMake.value', selectedVehicle?.make);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleModel.value', selectedVehicle?.model);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleVIN.value', selectedVehicle?.vin);
        } else {
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleBusinessSegment.value', undefined);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleType.value', undefined);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleYear.value', undefined);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleMake.value', undefined);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleModel.value', undefined);
            _.set(selectedPriorLoss, 'paPriorLoss.vehicleVIN.value', undefined);
        }
    };

    const isRPCEffective = rpcNumber =>
        WMICRPCUtil.getIsRPCEffective(
            jobVM?.baseData?.baseState?.value?.code,
            jobVM?.baseData?.rateAsOfDate?.value,
            rpcNumber
        );

    const isVehicleReadOnly =
        selectedPriorLoss?.value?.isReadOnly !== false ||
        !isEditing ||
        isExistingVehicleSelected() ||
        isNoVehicleSelected();

    const shouldSetAtFaultValuesForRPC1611 = () => {
        const baseState = jobVM?.baseData?.baseState?.value?.code;
        const rateAsOfDate = jobVM?.baseData?.rateAsOfDate?.value;
        const manualLossType = selectedPriorLoss?.manualLossType?.value?.code;

        return (
            manualLossType &&
            rateAsOfDate &&
            baseState &&
            manualLossType === PAConstants.directCompensationManualLossTypeCode &&
            WMICRPCUtil.getIsRPCEffective(baseState, rateAsOfDate, '1611')
        );
    };

    const getAtFaultOptions = () => {
        let atFaultValues = selectedPriorLoss?.paPriorLoss?.atFault?.aspects?.availableValues ?? [];

        if (selectedPriorLoss?.value?.isReadOnly === false && isEditing && shouldSetAtFaultValuesForRPC1611()) {
            atFaultValues = viewModelService.productMetadata
                .get('pc')
                .types.getTypelist('AtFaultIndicator_WMIC')
                .getFilter('DCPDAtRiskOptions').codes;
        }

        return atFaultValues.map(item => ({
            code: item.code,
            name: translator({ id: item.name }),
        }));
    };

    const onManualLossTypeChanged = (value, path) => {
        _.set(selectedPriorLoss, path, value);
        if (
            !getAtFaultOptions()
                ?.map(atFault => atFault.code)
                ?.includes(selectedPriorLoss?.paPriorLoss?.atFault?.value?.code)
        ) {
            selectedPriorLoss.paPriorLoss.atFault.value = null;
        }
        refreshData();
    };

    const onVehiclePublicIdChanged = (value, path) => {
        _.set(selectedPriorLoss, path, value);
        updatePriorLossVehicleInfo();
        refreshData();
    };

    const canShowAddPayoutButton = () => isEditing && selectedPriorLoss?.value?.isReadOnly === false;

    const renderAddPayoutButton = ({ isEditMode, onClick }) =>
        canShowAddPayoutButton() && (
            <WMICButton
                id="addPayoutButton"
                type="secondary"
                onClick={onClick}
                disabled={isEditMode || !isVehicleValid}
                icon="mi-add"
                className="gw-mb-3"
            >
                {translator(messages.addPayoutButton)}
            </WMICButton>
        );

    const getVehicleChoices = () => {
        const vehicles = [];

        _.each(availableVehicles, vehicle => {
            vehicles.push({
                code: vehicle.publicID,
                name: vehicle.displayName,
            });
        });

        vehicles.push({
            code: vehicleChoiceOther,
            name: translator(messages.vehicleOther),
        });

        return vehicles;
    };

    const getManualLossTypeAvailableValues = () =>
        manualLossTypeAvailableValues
            ?.map(lossType => ({
                code: lossType.code,
                name: translator({id: lossType.name}),
            }))
            ?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')) ?? [];

    const resolvers = {
        resolveCallbackMap: {
            onManualLossTypeChanged,
            onVehiclePublicIdChanged,
        },
        resolveComponentMap: {
            WMICAddPayoutComponent,
        },
    };

    const overrideProps = {
        '@field': {
            parentNode: selectedPriorLoss,
            readOnly: selectedPriorLoss?.value?.isReadOnly !== false || !isEditing,
        },
        payoutListView: {
            value: _.get(selectedPriorLoss, 'payments.children', []),
            clickable: isEditing,
            startOpen: false,
            VMData: [
                {
                    headerText: translator(messages.causeOfLoss),
                    getData: getTypeDisplayName,
                },
                {
                    headerText: translator(messages.payoutStatus),
                    getData: getStatusDisplayName,
                },
                {
                    headerText: translator(messages.claimOpenReserves),
                    getData: getClaimOpenReservesDisplayName,
                },
                {
                    headerText: translator(messages.lossTotalPaid),
                    getData: getLossTotalPaidDisplayName,
                },
            ],
            toCreate: addNewPayout,
            onSave: savePayout,
            onDelete: removePayout,
            toUndoCreate: () => {
                const payments = selectedPriorLoss?.value?.payments;
                payments.splice(payments.length - 1, 1);
                _.set(selectedPriorLoss.value, 'payments', payments);
            },
            detailViewComponent: WMICAddPayoutComponent,
            onValidate,
            renderAddButton: renderAddPayoutButton,
            detailViewComponentProps: {
                selectedPriorLoss,
                claimPaymentTypeAvailableValues,
            },
            flatCards: true,
            canEdit: () => selectedPriorLoss?.value?.isReadOnly === false,
            canDelete: () => selectedPriorLoss?.value?.isReadOnly === false,
            readOnly: selectedPriorLoss?.value?.isReadOnly !== false || !isEditing,
        },
        manualLossType: {
            availableValues: getManualLossTypeAvailableValues(),
        },
        insuranceCompany: {
            availableValues: carriersList,
            controlType: 'dropdown',
        },
        vehicleOperatorName: {
            availableValues: vehicleOperators,
            controlType: 'dropdown',
        },
        atFault: {
            availableValues: getAtFaultOptions(),
        },
        vehiclePublicID: {
            availableValues: getVehicleChoices(),
            controlType: 'dropdown',
        },
        payoutOrReserveRequiredMsg: {
            visible: isEditing && isVehicleValid && !isMinimumPayoutValid(),
        },
        hitAndRun: {
            visible: isRPCEffective('1265'),
        },
        vehicleBusinessSegment: {
            readOnly: isVehicleReadOnly,
        },
        vehicleType: {
            readOnly: isVehicleReadOnly,
        },
        vehicleYear: {
            readOnly: isVehicleReadOnly,
        },
        vehicleMake: {
            readOnly: isVehicleReadOnly,
        },
        vehicleModel: {
            readOnly: isVehicleReadOnly,
        },
        vehicleVIN: {
            readOnly: isVehicleReadOnly,
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={selectedPriorLoss}
            overrideProps={overrideProps}
            onModelChange={() => refreshData()}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            onValidationChange={setComponentValidation}
            showErrors={showErrors}
        />
    );
}

WMICAddLossHistoryComponent.propTypes = {
    id: PropTypes.string.isRequired,
    selectedPriorLoss: PropTypes.shape({}).isRequired,
    onValidate: PropTypes.func.isRequired,
    jobVM: PropTypes.shape({}).isRequired,
    isEditing: PropTypes.bool.isRequired,
    isAdding: PropTypes.bool.isRequired,
    carriersList: PropTypes.array.isRequired,
    vehicleOperators: PropTypes.array.isRequired,
    showErrors: PropTypes.bool.isRequired,
};

export default WMICAddLossHistoryComponent;
